import React, { useState, useEffect } from "react";
import fetchData from "../../api/fetchApi";
import InternationalPhoneField from "../forms/InternationalPhoneField";
import { isInternationalPhoneValid, phoneForApi } from "../../utils/phone";

export default function SendMessagePage() {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [mediaFile, setMediaFile] = useState(null);

  // New: For client/group selection
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedType, setSelectedType] = useState("manual"); // manual, client, group

  useEffect(() => {
    fetchData("user/clients")
      .then((data) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
    fetchData("user/groups")
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .catch(() => setGroups([]));
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (selectedType === "manual" && !isInternationalPhoneValid(to)) {
      setError("أدخل رقم الهاتف بالصيغة الدولية الصحيحة.");
      return;
    }
    setSending(true);
    setSuccess("");
    setError("");
    try {
      let image_base64 = "";
      if (mediaFile) {
        image_base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(mediaFile);
        });
      }

      let payload;
      if (selectedType === "manual") {
        payload = {
          phone: [phoneForApi(to)],
          message,
          image_base64
        };
      } else if (selectedType === "client") {
        // Find client by phone to get uuid
        const clientObj = clients.find(c => (c.phone_number || c.phone) === to);
        payload = {
          list_users: [clientObj?.uuid || to],
          message,
          image_base64
        };
      } else if (selectedType === "group") {
        // Send to group by uuid (already selected)
        payload = {
          group: groups.find(g => g.uuid === to)?.uuid || to,
          message,
          image_base64
        };
      }

      const response = await fetchData(
        `/message`,
        "POST",
        JSON.stringify(payload)
      );

      if (response && response.success === false && response.error) {
        throw new Error(response.error);
      }

      // Fix: handle both object and string for response, and avoid undefined property access
      let clientCount = 0;
      let estimatedTime = "";
      let msg = "تم إرسال الرسالة بنجاح";
      if (response) {
        if (typeof response === "object") {
          clientCount = response.client_count || (response.body && response.body.client_count) || 0;
          estimatedTime = response.estimated_time || (response.body && response.body.estimated_time) || "";
          msg = response.message || msg;
        }
      }

      setSuccess(
        <>
          <div>{msg}</div>
          <div>عدد العملاء: {clientCount}</div>
          <div>الوقت المتوقع: {estimatedTime}</div>
        </>
      );
      setTo("");
      setMessage("");
      setMediaFile(null);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء الإرسال");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="panel-card mx-auto w-full max-w-4xl">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-950 mb-2">
        إرسال رسالة جديدة
      </h2>
      <div className="mb-4 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-3">
        يمكنك استخدام <span className="font-mono bg-gray-100 px-2 py-1 rounded">{`{name}`}</span> في نص الرسالة ليتم استبداله باسم العميل تلقائياً عند الإرسال إلى عميل أو مجموعة.
        <br />
        <span className="text-gray-600">مثال: <span className="font-mono bg-gray-100 px-2 py-1 rounded">مرحباً {`{name}`}, كيف حالك؟</span></span>
      </div>
      <form onSubmit={handleSend} className="space-y-4">
        <div>
          <label className="block mb-1 font-medium">طريقة التحديد</label>
          <select
            className="w-full border rounded px-3 py-2 mb-2"
            value={selectedType}
            onChange={e => {
              setSelectedType(e.target.value);
              setTo("");
            }}
          >
            <option value="manual">إدخال يدوي</option>
            <option value="client">اختر عميل من القائمة</option>
            <option value="group">اختر مجموعة</option>
          </select>
        </div>
        {selectedType === "manual" && (
          <div>
            <InternationalPhoneField
              id="message-recipient-phone"
              label="رقم المستلم"
              value={to}
              onChange={setTo}
              required={selectedType === "manual"}
              helperText="اختر دولة المستلم، ثم اكتب الرقم المحلي."
            />
          </div>
        )}
        {selectedType === "client" && (
          <div>
            <label className="block mb-1 font-medium">اختر عميل</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={to}
              onChange={e => setTo(e.target.value)}
              required={selectedType === "client"}
            >
              <option value="">اختر عميل</option>
              {clients.map((c, idx) => (
                <option key={c.uuid || idx} value={c.phone_number || c.phone}>
                  {c.name ? `${c.name} (${c.phone_number || c.phone})` : (c.phone_number || c.phone)}
                </option>
              ))}
            </select>
          </div>
        )}
        {selectedType === "group" && (
          <div>
            <label className="block mb-1 font-medium">اختر مجموعة</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={to}
              onChange={e => setTo(e.target.value)}
              required={selectedType === "group"}
            >
              <option value="">اختر مجموعة</option>
              {groups.map((g, idx) => (
                <option key={g.uuid || idx} value={g.uuid}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block mb-1 font-medium">نص الرسالة</label>
          <textarea
            className="w-full border rounded px-3 py-2"
            value={message}
            onChange={e => setMessage(e.target.value)}
            required={!mediaFile}
            rows={6}
            placeholder="اكتب رسالتك هنا"
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">رفع صورة (اختياري)</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="w-full border rounded px-3 py-2"
            onChange={e => {
              const file = e.target.files[0];
              if (file && file.size > 5 * 1024 * 1024) {
                setError("حجم الصورة يجب ألا يتجاوز 5 ميجابايت.");
                e.target.value = "";
                setMediaFile(null);
                return;
              }
              setError("");
              setMediaFile(file || null);
            }}
          />
          {mediaFile && (
            <div className="mt-2 text-sm text-gray-600">
              الملف المختار: {mediaFile.name}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="primary-button primary-button-auto"
          disabled={sending || !to || (!message.trim() && !mediaFile)}
        >
          {sending ? "جاري الإرسال..." : "إرسال"}
        </button>
        {success && <div className="notice notice-success mt-2" role="status">{success}</div>}
        {error && <div className="notice notice-error mt-2" role="alert">{error}</div>}
      </form>
    </div>
  );
}
